import useAlert from "@hooks/AlertHook"
import { useState } from "react"

type ShareInputsProps = {
    shares: { [key: string]: number },
    creator?: string,
    onChange?: (shares: { [key: string]: number }) => void
}

export function ShareInputs({ shares, creator, onChange }: ShareInputsProps) {
    const burnAddress = "terra1sk06e3dyexuq4shw77y3dsv480xv42mq73anxu"

    const alert = useAlert()
    const [shareAddress, setShareAddress] = useState("")
    const [shareAmount, setShareAmount] = useState("")

    const onAdd = () => {
        const amount = Number.parseFloat(shareAmount)
        const maxAmount = 90 - Object.values(shares).reduce((prev, curr) => prev + curr, 0) + (shares[shareAddress] ?? 0);

        if (!shareAddress) {
            alert.failed("Address cannot be empty")
            return
        }

        if (isNaN(amount)) {
            alert.failed(`Amount must be a number between 0 and ${maxAmount}, use '.' as decimal separator`)
            return
        }

        if (amount <= 0) {
            alert.failed("Amount must be greater than 0%")
            return
        }

        if (amount > maxAmount) {
            alert.failed("Amount cannot be greater than " + maxAmount)
            return
        }

        shares[shareAddress] = amount
        onChange!(shares)
        setShareAddress("")
        setShareAmount("")
    }

    const onDelete = (address: string) => {
        onChange!(Object.keys(shares).filter(key => key !== address).reduce((prev, curr) => ({ ...prev, [curr]: shares[curr] }), {}))
    }

    const onUpdate = (address: string) => {
        setShareAddress(address)
        setShareAmount(shares[address].toString())
    }

    return <>
        {
            Object.keys(shares).map(address => {
                return <div
                    key={address}
                    className="flex flex-shrink-0 items-center mt-1 text-xs font-medium text-jacarta-500 dark:text-jacarta-300 sm:text-sm">
                    <div
                        className="flex items-center w-full text-xs font-medium text-jacarta-500 dark:text-jacarta-300 sm:text-sm">
                        {
                            address === burnAddress && <div
                                className="flex font-bold h-10 items-center border-r-0 border border-jacarta-100 bg-white p-3 first:rounded-l-lg last:rounded-r-lg dark:border-gray-400 dark:bg-gray-900 sm:px-4 sm:py-2">
                                <span className="animate-gradient">Burn</span>
                            </div>
                        }
                        {
                            address === creator && <div
                                className="flex text-green font-bold h-10 items-center border-r-0 border border-jacarta-100 bg-white p-3 first:rounded-l-lg last:rounded-r-lg dark:border-gray-400 dark:bg-gray-900 sm:px-4 sm:py-2">
                                Creator
                            </div>
                        }
                        <div
                            className="flex h-10 w-full items-center border-r-0 border border-jacarta-100 bg-white p-3 first:rounded-l-lg last:rounded-r-lg dark:border-gray-400 text-white dark:bg-gray-900 sm:px-4 sm:py-2">
                            {address}
                        </div>
                        <div
                            className="flex h-10 items-center whitespace-nowrap border border-jacarta-100 bg-white p-3 first:rounded-l-lg last:rounded-r-lg dark:border-gray-400 text-white dark:bg-gray-900 sm:px-4 sm:py-2">
                            {shares[address]}%
                        </div>
                    </div>
                    {
                        onChange &&
                        <div
                            className="ml-2 flex items-center text-xs font-medium text-jacarta-500 dark:text-jacarta-300 sm:text-sm">
                            <button
                                onClick={() => onUpdate(address)}
                                className="flex h-10 w-10 group cursor-pointer items-center justify-center border-r-0 first:rounded-l-lg last:rounded-r-lg dark:bg-gray-900 dark:border-gray-400 border border-jacarta-100 bg-white dark:hover:bg-jacarta-500 dark:hover:border-transparent hover:bg-jacarta-500 hover:border-transparent">
                                <svg className="h-4 w-4 dark:fill-gray-400 group-hover:fill-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16"><path d="M1.172,19.119A4,4,0,0,0,0,21.947V24H2.053a4,4,0,0,0,2.828-1.172L18.224,9.485,14.515,5.776Z" /><path d="M23.145.855a2.622,2.622,0,0,0-3.71,0L15.929,4.362l3.709,3.709,3.507-3.506A2.622,2.622,0,0,0,23.145.855Z" /></svg>
                            </button>
                            <button
                                onClick={() => onDelete(address)}
                                className="flex h-10 w-10 group cursor-pointer items-center justify-center first:rounded-l-lg last:rounded-r-lg dark:bg-gray-900 dark:border-gray-400 border border-jacarta-100 bg-white dark:hover:bg-jacarta-500 dark:hover:border-transparent hover:bg-accent hover:border-accent">
                                <svg className="h-4 w-4 fill-jacarta-700 dark:fill-gray-400 group-hover:fill-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16"><path d="M23,3H18V2.5A2.5,2.5,0,0,0,15.5,0h-7A2.5,2.5,0,0,0,6,2.5V3H1V6H3V21a3,3,0,0,0,3,3H18a3,3,0,0,0,3-3V6h2ZM18,21H6V6H18Z" /><rect x="8" y="9" width="3" height="9" /><rect x="13" y="9" width="3" height="9" /></svg>
                            </button>
                        </div>
                    }
                </div>
            })
        }
        {
            onChange &&
            <div
                className="flex flex-shrink-0 items-center mt-5 text-xs font-medium text-jacarta-500 dark:text-jacarta-700 sm:text-sm">
                <div
                    className="flex items-center w-full text-xs font-medium sm:text-sm mr-2">
                    <input
                        value={shareAddress}
                        onChange={e => setShareAddress(e.target.value)}
                        placeholder="Wallet Address"
                        className="text-white flex h-10 w-full items-center border border-jacarta-100 bg-white p-3 first:rounded-l-lg focus:ring-jacarta-500 hover:ring-2 hover:ring-jacarta-500/10 last:rounded-r-lg placeholder:text-gray-400 dark:border-gray-400 dark:bg-gray-900 sm:px-4 sm:py-2" />
                </div>
                <div
                    className="flex items-center text-xs font-medium sm:text-sm mr-2 text-white">
                    <input
                        value={shareAmount}
                        onChange={({ target }) => setShareAmount(target.value)}
                        placeholder="100"
                        className="flex h-10 max-w-[4rem] items-center text-white whitespace-nowrap border-r-0 border border-jacarta-100 hover:ring-2 hover:ring-jacarta-500/10 focus:ring-jacarta-500 bg-white p-3 first:rounded-l-lg last:rounded-r-lg placeholder:text-gray-400 dark:border-gray-400 dark:bg-gray-900 sm:px-4 sm:py-2" />
                    <div
                        className="flex h-10 items-center text-white whitespace-nowrap border border-jacarta-100 bg-white p-3 first:rounded-l-lg last:rounded-r-lg dark:border-gray-400 dark:bg-gray-900 sm:px-4 sm:py-2">
                        %
                    </div>
                </div>
                <div
                    className="flex items-center text-xs font-medium text-jacarta-500 dark:text-jacarta-300 sm:text-sm">
                    <button
                        onClick={onAdd}
                        className="flex h-10 fill-gray-400 hover:fill-white w-10 group cursor-pointer items-center justify-center first:rounded-l-lg last:rounded-r-lg dark:bg-gray-900 dark:border-gray-400 border border-jacarta-100 bg-white dark:hover:bg-jacarta-500 hover:bg-accent dark:hover:border-transparent">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            width="24"
                            height="24"
                        >
                            <path fill="none" d="M0 0h24v24H0z" />
                            <path d="M11 11V5h2v6h6v2h-6v6h-2v-6H5v-2z" />
                        </svg>
                    </button>
                </div>
            </div>
        }
    </>
}